import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useToast } from '@/hooks/use-toast';
import { diagnosisService } from '@/services/diagnosisService';
import { patientService } from '@/services/patientService';
import { DiagnosisForm } from '@/components/forms/DiagnosisForm';
import { 
  Diagnosis, 
  DiagnosisStatistics,
  ICD10Option 
} from '@/types/diagnosis.types';
import { Patient } from '@/types/patient.types';
import {
  Heart,
  AlertCircle,
  FileDown,
  Search,
  Filter,
  Edit,
  Trash2,
  Download,
  Calendar,
  Stethoscope,
  Crown,
  X,
  UserPlus,
  Users,
} from 'lucide-react';
import { formatDate } from '@/utils/formatters';

interface DiagnosisFilters {
  search: string;
  icd10_code: string;
  severity: string;
  date_from: string;
  date_to: string;
  chd_subgroup: string;
  is_primary: string;
}

interface DiagnosisWithPatient extends Diagnosis {
  // patient_name and patient_code are now part of Diagnosis interface from backend
  patient_cnp?: string; // This is not provided by backend getAllDiagnoses, needs separate fetch
}

interface PatientSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPatient: (patient: Patient) => void;
}

const PatientSelector: React.FC<PatientSelectorProps> = ({
  isOpen,
  onClose,
  onSelectPatient
}) => {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);

  // Load patients when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadPatients();
    }
  }, [isOpen]);

  // Filter patients based on search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredPatients(patients);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredPatients(
        patients.filter(
          (patient) =>
            patient.name?.toLowerCase().includes(query) ||
            patient.cnp?.toLowerCase().includes(query) ||
            patient.patient_code?.toLowerCase().includes(query)
        )
      );
    }
  }, [searchQuery, patients]);

  const loadPatients = async () => {
    try {
      setIsLoading(true);
      const response = await patientService.getPatients();
      setPatients(response.data || []);
    } catch (error: any) {
      console.error('Error loading patients:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectPatient = (patient: Patient) => {
    onSelectPatient(patient);
    onClose();
    setSearchQuery('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Selectați pacientul pentru diagnostic
          </DialogTitle>
          <DialogDescription>
            Căutați și selectați pacientul pentru care doriți să adăugați un diagnostic nou.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Căutați după nume, CNP sau cod pacient..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Patient List */}
          <div className="flex-1 overflow-y-auto border rounded-lg">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : filteredPatients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {searchQuery ? 'Nu au fost găsiți pacienți' : 'Nu există pacienți în sistem'}
              </div>
            ) : (
              <div className="space-y-2 p-4">
                {filteredPatients.map((patient) => (
                  <div
                    key={patient.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleSelectPatient(patient)}
                  >
                    <div className="flex-1">
                      <div className="font-medium">{patient.name}</div>
                      <div className="text-sm text-muted-foreground">
                        CNP: {patient.cnp || 'Nespecificat'} | 
                        Cod: {patient.patient_code} | 
                        Data nașterii: {formatDate(patient.birth_date)}
                      </div>
                    </div>
                    <Button variant="outline" size="sm">
                      Selectează
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const DiagnosticsPage: React.FC = () => {
  const { toast } = useToast();
  
  // State
  const [diagnoses, setDiagnoses] = useState<DiagnosisWithPatient[]>([]);
  const [statistics, setStatistics] = useState<DiagnosisStatistics | null>(null);
  const [icd10Codes, setIcd10Codes] = useState<ICD10Option[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  
  // Dialog states
  const [isPatientSelectorOpen, setIsPatientSelectorOpen] = useState(false);
  const [isDiagnosisFormOpen, setIsDiagnosisFormOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [editingDiagnosis, setEditingDiagnosis] = useState<Diagnosis | null>(null);
  
  // Filter states
  const [filters, setFilters] = useState<DiagnosisFilters>({
    search: '',
    icd10_code: '',
    severity: '',
    date_from: '',
    date_to: '',
    chd_subgroup: '',
    is_primary: '',
  });
  const [filteredDiagnoses, setFilteredDiagnoses] = useState<DiagnosisWithPatient[]>([]);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  // Apply filters whenever diagnoses or filters change
  useEffect(() => {
    applyFilters();
  }, [diagnoses, filters]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [diagnosesData, statisticsData, icd10Data] = await Promise.all([
        diagnosisService.getAllDiagnoses(),
        diagnosisService.getDiagnosisStatistics(),
        diagnosisService.getICD10Codes(),
      ]);

      // Backend already provides patient_name and patient_code
      // No need to fetch each patient separately
      const enhancedDiagnoses = diagnosesData.map((diagnosis) => ({
        ...diagnosis,
        // Use patient data from backend or provide defaults
        patient_name: diagnosis.patient_name || 'Necunoscut',
        patient_cnp: 'N/A', // CNP is not provided by backend getAllDiagnoses
        patient_code: diagnosis.patient_code || 'N/A',
      } as DiagnosisWithPatient));

      setDiagnoses(enhancedDiagnoses);
      setStatistics(statisticsData);
      setIcd10Codes(icd10Data);
    } catch (error: any) {
      console.error('Error loading data:', error);
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-au putut încărca datele',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...diagnoses];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (diagnosis) =>
          diagnosis.patient_name?.toLowerCase().includes(searchLower) ||
          diagnosis.patient_cnp?.toLowerCase().includes(searchLower) ||
          diagnosis.patient_code?.toLowerCase().includes(searchLower) ||
          diagnosis.icd10_code.toLowerCase().includes(searchLower) ||
          (diagnosis.diagnosis_name_ro?.toLowerCase().includes(searchLower) || 
           diagnosis.diagnosis_name_en?.toLowerCase().includes(searchLower))
      );
    }

    // ICD-10 code filter
    if (filters.icd10_code && filters.icd10_code !== 'all') {
      filtered = filtered.filter((diagnosis) =>
        diagnosis.icd10_code.startsWith(filters.icd10_code)
      );
    }

    // Severity filter
    if (filters.severity && filters.severity !== 'all') {
      filtered = filtered.filter(
        (diagnosis) => (diagnosis.severity_level || diagnosis.severity_id || 3).toString() === filters.severity
      );
    }

    // Date range filter
    if (filters.date_from) {
      filtered = filtered.filter(
        (diagnosis) => new Date(diagnosis.diagnosis_date) >= new Date(filters.date_from)
      );
    }
    if (filters.date_to) {
      filtered = filtered.filter(
        (diagnosis) => new Date(diagnosis.diagnosis_date) <= new Date(filters.date_to)
      );
    }

    // Primary filter
    if (filters.is_primary && filters.is_primary !== 'all') {
      const isPrimary = filters.is_primary === 'true';
      filtered = filtered.filter((diagnosis) => diagnosis.is_primary === isPrimary);
    }

    // MCC subgroup filter - we'll implement this when we have subgroup data
    if (filters.chd_subgroup && filters.chd_subgroup !== 'all') {
      filtered = filtered.filter(
        (diagnosis) => diagnosis.eurocat_subgroup === filters.chd_subgroup
      );
    }

    setFilteredDiagnoses(filtered);
  };

  const handleFilterChange = (key: keyof DiagnosisFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      icd10_code: '',
      severity: '',
      date_from: '',
      date_to: '',
      chd_subgroup: '',
      is_primary: '',
    });
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).filter((value) => value !== '' && value !== 'all').length;
  };

  // Handle diagnosis operations
  const handlePatientSelected = (patient: Patient) => {
    setSelectedPatient(patient);
    setEditingDiagnosis(null);
    setIsDiagnosisFormOpen(true);
  };

  const handleEditDiagnosis = (diagnosis: Diagnosis) => {
    // First get the patient for this diagnosis
    patientService.getPatient(diagnosis.patient_id)
      .then((patient) => {
        setSelectedPatient(patient);
        setEditingDiagnosis(diagnosis);
        setIsDiagnosisFormOpen(true);
      })
      .catch((error) => {
        toast({
          title: 'Eroare',
          description: 'Nu s-au putut încărca datele pacientului',
          variant: 'destructive',
        });
      });
  };

  const handleDeleteDiagnosis = async (diagnosis: Diagnosis) => {
    if (!window.confirm('Sunteți sigur că doriți să ștergeți acest diagnostic?')) {
      return;
    }

    try {
      await diagnosisService.deleteDiagnosis(diagnosis.id);
      toast({
        title: 'Succes',
        description: 'Diagnosticul a fost șters cu succes',
      });
      await loadData();
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'Nu s-a putut șterge diagnosticul',
        variant: 'destructive',
      });
    }
  };

  const handleDiagnosisSubmit = async (data: any) => {
    try {
      if (editingDiagnosis) {
        await diagnosisService.updateDiagnosis(editingDiagnosis.id, data);
      } else {
        await diagnosisService.createDiagnosis(data);
      }
      
      setIsDiagnosisFormOpen(false);
      setSelectedPatient(null);
      setEditingDiagnosis(null);
      await loadData();
    } catch (error: any) {
      throw error; // Let DiagnosisForm handle the error display
    }
  };

  // Export functionality
  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      setIsExporting(true);
      
      // Prepare data for export
      const exportData = filteredDiagnoses.map((diagnosis) => ({
        'Pacient': diagnosis.patient_name || '',
        'CNP': diagnosis.patient_cnp || '',
        'Cod pacient': diagnosis.patient_code || '',
        'Cod ICD-10': diagnosis.icd10_code,
        'Diagnostic': diagnosis.diagnosis_name_ro || diagnosis.diagnosis_name_en || diagnosis.icd10_code,
        'Subgrup MCC': diagnosis.eurocat_subgroup || '',
        'Severitate': getSeverityLabel(diagnosis.severity_level || diagnosis.severity_id || 3),
        'Principal': diagnosis.is_primary ? 'Da' : 'Nu',
        'Data diagnostic': formatDate(diagnosis.diagnosis_date),
        'Observații': diagnosis.notes || '',
      }));

      // Create and download file
      const filename = `diagnostice_${new Date().toISOString().split('T')[0]}`;
      
      if (format === 'csv') {
        downloadAsCSV(exportData, filename);
      } else {
        // For Excel, we'll use a simple CSV with .xlsx extension
        // In a real app, you'd use a library like xlsx
        downloadAsCSV(exportData, filename, 'xlsx');
      }

      toast({
        title: 'Succes',
        description: `Fișierul ${format.toUpperCase()} a fost descărcat cu succes`,
      });
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut exporta fișierul',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const downloadAsCSV = (data: any[], filename: string, extension: string = 'csv') => {
    if (data.length === 0) return;

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map((row) =>
        headers.map((header) => `"${(row[header] || '').toString().replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.${extension}`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper functions
  const getSeverityLabel = (level: number): string => {
    const labels = {
      1: 'Ușor',
      2: 'Moderat', 
      3: 'Sever',
      4: 'Critic',
      5: 'Foarte critic'
    };
    return labels[level as keyof typeof labels] || 'Necunoscut';
  };

  const getSeverityBadge = (level: number) => {
    const configs = {
      1: { color: 'bg-green-100 text-green-800', label: 'Ușor' },
      2: { color: 'bg-yellow-100 text-yellow-800', label: 'Moderat' },
      3: { color: 'bg-orange-100 text-orange-800', label: 'Sever' },
      4: { color: 'bg-red-100 text-red-800', label: 'Critic' },
      5: { color: 'bg-purple-100 text-purple-800', label: 'Foarte critic' },
    };
    
    const config = configs[level as keyof typeof configs] || configs[3];
    
    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // Data table columns
  const columns = [
    {
      name: 'Pacient',
      selector: (row: DiagnosisWithPatient) => row.patient_name || '',
      sortable: true,
      grow: 1.5,
      cell: (row: DiagnosisWithPatient) => (
        <div>
          <div className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer">
            {row.patient_name || 'Necunoscut'}
          </div>
          <div className="text-xs text-muted-foreground">
            {row.patient_code}
          </div>
        </div>
      ),
    },
    {
      name: 'CNP',
      selector: (row: DiagnosisWithPatient) => row.patient_cnp || '',
      sortable: true,
      width: '140px',
      cell: (row: DiagnosisWithPatient) => (
        <span className="font-mono text-sm">{row.patient_cnp || 'N/A'}</span>
      ),
    },
    {
      name: 'Cod ICD-10',
      selector: (row: DiagnosisWithPatient) => row.icd10_code,
      sortable: true,
      width: '120px',
      cell: (row: DiagnosisWithPatient) => (
        <span className="font-mono font-medium">{row.icd10_code}</span>
      ),
    },
    {
      name: 'Diagnostic',
      selector: (row: DiagnosisWithPatient) => row.diagnosis_name_ro || row.diagnosis_name_en || row.icd10_code,
      sortable: true,
      grow: 2,
      cell: (row: DiagnosisWithPatient) => (
        <div>
          <div className="font-medium">{row.diagnosis_name_ro || row.diagnosis_name_en || row.icd10_code}</div>
          {row.notes && (
            <div className="text-xs text-muted-foreground mt-1 truncate">
              {row.notes}
            </div>
          )}
        </div>
      ),
    },
    {
      name: 'Subgrup MCC',
      selector: (row: DiagnosisWithPatient) => row.eurocat_subgroup || '',
      sortable: true,
      width: '140px',
      cell: (row: DiagnosisWithPatient) => (
        <span className="text-sm">
          {row.eurocat_subgroup || 'Nespecificat'}
        </span>
      ),
    },
    {
      name: 'Severitate',
      selector: (row: DiagnosisWithPatient) => row.severity_level || row.severity_id || 3,
      sortable: true,
      width: '120px',
      cell: (row: DiagnosisWithPatient) => getSeverityBadge(row.severity_level || row.severity_id || 3),
    },
    {
      name: 'Principal',
      selector: (row: DiagnosisWithPatient) => row.is_primary,
      sortable: true,
      width: '100px',
      cell: (row: DiagnosisWithPatient) => (
        <div className="flex items-center justify-center">
          {row.is_primary ? (
            <Badge className="bg-blue-100 text-blue-800">
              <Crown className="h-3 w-3 mr-1" />
              Da
            </Badge>
          ) : (
            <Badge variant="outline">Nu</Badge>
          )}
        </div>
      ),
    },
    {
      name: 'Data diagnostic',
      selector: (row: DiagnosisWithPatient) => row.diagnosis_date,
      sortable: true,
      width: '130px',
      cell: (row: DiagnosisWithPatient) => (
        <div className="flex items-center gap-1">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span className="text-sm">
            {formatDate(row.diagnosis_date)}
          </span>
        </div>
      ),
    },
    {
      id: 'actions',
      name: 'Acțiuni',
      button: true,
      width: '140px',
      cell: (row: DiagnosisWithPatient) => (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditDiagnosis(row)}
            className="h-8 w-8 p-0"
            title="Editează"
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteDiagnosis(row)}
            className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
            title="Șterge"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        backgroundColor: '#f8fafc',
        color: '#475569',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
    },
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Diagnostice</h1>
            <p className="text-muted-foreground">
              Gestionați diagnosticele MCC din sistem
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={isExporting || filteredDiagnoses.length === 0}
            >
              <FileDown className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('excel')}
              disabled={isExporting || filteredDiagnoses.length === 0}
            >
              <Download className="mr-2 h-4 w-4" />
              Export Excel
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total diagnostice</CardTitle>
                <Stethoscope className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{statistics?.total_diagnoses || 0}</div>
                <p className="text-xs text-muted-foreground">
                  Toate diagnosticele din sistem
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pacienți cu diagnostice</CardTitle>
                <Users className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {statistics?.patients_with_diagnoses || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Pacienți unici diagnosticați
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">MCC severe</CardTitle>
                <Heart className="h-4 w-4 text-red-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">
                  {statistics?.severe_chd_diagnoses || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Diagnostice MCC severe
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Diagnostice principale</CardTitle>
                <Crown className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">
                  {statistics?.primary_diagnoses || 0}
                </div>
                <p className="text-xs text-muted-foreground">
                  Din {statistics?.total_diagnoses || 0} total
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filtre
              </CardTitle>
              {getActiveFilterCount() > 0 && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {getActiveFilterCount()} filtru{getActiveFilterCount() !== 1 ? 'ri' : ''} activ{getActiveFilterCount() !== 1 ? 'e' : ''}
                  </Badge>
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" />
                    Șterge
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Căutare</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Nume pacient sau CNP..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* ICD-10 Code */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Cod ICD-10</label>
                <Select
                  value={filters.icd10_code}
                  onValueChange={(value) => handleFilterChange('icd10_code', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toate codurile" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate codurile</SelectItem>
                    {icd10Codes.map((code) => (
                      <SelectItem key={code.code} value={code.code}>
                        {code.code} - {code.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Severity */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Severitate</label>
                <Select
                  value={filters.severity}
                  onValueChange={(value) => handleFilterChange('severity', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate</SelectItem>
                    <SelectItem value="1">Ușor</SelectItem>
                    <SelectItem value="2">Moderat</SelectItem>
                    <SelectItem value="3">Sever</SelectItem>
                    <SelectItem value="4">Critic</SelectItem>
                    <SelectItem value="5">Foarte critic</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Primary */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Principal</label>
                <Select
                  value={filters.is_primary}
                  onValueChange={(value) => handleFilterChange('is_primary', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toate" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate</SelectItem>
                    <SelectItem value="true">Da</SelectItem>
                    <SelectItem value="false">Nu</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date From */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Data de la</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => handleFilterChange('date_from', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Date To */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Data până la</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => handleFilterChange('date_to', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* MCC Subgroup */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Subgrup MCC</label>
                <Select
                  value={filters.chd_subgroup}
                  onValueChange={(value) => handleFilterChange('chd_subgroup', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Toate subgrupurile" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toate subgrupurile</SelectItem>
                    {/* We'll populate this with unique subgroups from the data */}
                    {Array.from(new Set(diagnoses.map(d => d.eurocat_subgroup).filter(Boolean)))
                      .map((subgroup) => (
                        <SelectItem key={subgroup} value={subgroup!}>
                          {subgroup}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5" />
                Lista diagnosticelor
                {filteredDiagnoses.length > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {filteredDiagnoses.length} rezultate
                  </Badge>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {filteredDiagnoses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                  Nu există diagnostice
                </h3>
                <p className="text-muted-foreground mb-4">
                  {getActiveFilterCount() > 0 
                    ? 'Nu au fost găsite diagnostice cu filtrele aplicate.'
                    : 'Nu există diagnostice în sistem.'
                  }
                </p>
                {getActiveFilterCount() > 0 && (
                  <Button variant="outline" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-2" />
                    Șterge filtrele
                  </Button>
                )}
              </div>
            ) : (
              <div className="diagnostics-table-sticky-actions">
                <style>{`
                  .diagnostics-table-sticky-actions [data-column-id="actions"] {
                    position: sticky;
                    right: 0;
                    z-index: 2;
                    background-color: #ffffff;
                    box-shadow: -2px 0 4px -2px rgba(0, 0, 0, 0.15);
                  }
                  .diagnostics-table-sticky-actions .rdt_TableCol[data-column-id="actions"] {
                    background-color: #f8fafc;
                  }
                `}</style>
                <DataTable
                  columns={columns}
                  data={filteredDiagnoses}
                  customStyles={customStyles}
                  highlightOnHover
                  responsive
                  pagination
                  paginationPerPage={20}
                  paginationRowsPerPageOptions={[10, 20, 50, 100]}
                  noDataComponent={
                    <div className="text-center py-4 text-muted-foreground">
                      Nu există diagnostice de afișat
                    </div>
                  }
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Patient Selector Dialog */}
        <PatientSelector
          isOpen={isPatientSelectorOpen}
          onClose={() => setIsPatientSelectorOpen(false)}
          onSelectPatient={handlePatientSelected}
        />

        {/* Diagnosis Form Dialog */}
        <Dialog open={isDiagnosisFormOpen} onOpenChange={setIsDiagnosisFormOpen}>
          <DialogContent className="flex max-h-[88vh] w-[min(94vw,48rem)] max-w-3xl flex-col overflow-hidden">
            <DialogHeader>
              <DialogTitle>
                {editingDiagnosis ? 'Editează diagnostic' : 'Adaugă diagnostic nou'}
              </DialogTitle>
              <DialogDescription>
                {editingDiagnosis 
                  ? 'Modificați informațiile diagnosticului selectat.'
                  : `Adăugați un diagnostic nou pentru pacientul ${selectedPatient?.name}.`
                }
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 overflow-y-auto pr-1">
              {selectedPatient && (
                <DiagnosisForm
                  patientId={selectedPatient.id}
                  diagnosis={editingDiagnosis}
                  onSubmit={handleDiagnosisSubmit}
                  onCancel={() => {
                    setIsDiagnosisFormOpen(false);
                    setSelectedPatient(null);
                    setEditingDiagnosis(null);
                  }}
                />
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};
